<template>
  <div class="col-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ t('guardians.edit.title') }}
            </h6>
          </div>
        </div>
      </div>

      <div class="card-body px-0 pb-2 pe-3">
        <div class="row justify-content-center">
          <div class="col-md-6"></div>
          
          <LoadingSpinner v-if="isLoading" :message="t('guardians.edit.loading')" />

          <GuardianForm
            v-else-if="guardian"
            ref="guardianForm"
            :initial-data="guardian"
            :is-loading="isUpdating"
            :is-edit-mode="true"
            :submit-text="t('guardians.edit.submit')"
            @submit="updateGuardian"
            @cancel="navigateBack"
            @update:form-data="handleFormUpdate"
          />

          <div class="d-flex gap-2 mt-3 ms-3">
            <button 
              type="button" 
              class="btn btn-primary btn-sm flex-grow-1"
              @click="submitForm"
              :disabled="isUpdating || !hasChanges"
            >
              <span v-if="isUpdating">
                <i class="fas fa-spinner fa-spin ms-2"></i>
                {{ t('guardians.edit.saving') }}
              </span>
              <span v-else>
                <i class="fas fa-edit ms-2"></i>
                {{ t('guardians.edit.submit') }}
              </span>
            </button>
            
            <button 
              type="button" 
              class="btn btn-outline-secondary btn-sm flex-grow-1"
              @click="navigateBack"
              :disabled="isUpdating"
            >
              <i class="fas fa-times ms-2"></i>
              {{ t('guardians.edit.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { useToast } from 'vue-toastification';
import { useI18n } from "vue-i18n";
import GuardianForm from "@/components/features/guardians/GuardianForm.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();
const toast = useToast();

const isLoading = ref(false);
const isUpdating = ref(false);
const guardianForm = ref(null);
const originalGuardianData = ref(null);
const currentFormData = ref(null);

const hasChanges = computed(() => {
  if (!originalGuardianData.value || !currentFormData.value) return false;
  return Object.keys(currentFormData.value).some(key => {
    if (key === 'password' || key === 'password_confirmation') return false;
    if (currentFormData.value[key] === null || originalGuardianData.value[key] === null) {
      return currentFormData.value[key] !== originalGuardianData.value[key];
    }
    if (key.includes('_date')) {
      const currentDate = currentFormData.value[key]?.split('T')[0] || currentFormData.value[key];
      const originalDate = originalGuardianData.value[key]?.split('T')[0] || originalGuardianData.value[key];
      return currentDate !== originalDate;
    }
    return JSON.stringify(currentFormData.value[key]) !== JSON.stringify(originalGuardianData.value[key]);
  });
});

const handleFormUpdate = (newData) => currentFormData.value = { ...newData };

const submitForm = async () => guardianForm.value?.submitForm();

const fetchGuardian = async () => {
  isLoading.value = true;
  try {
    await store.dispatch("guardian/fetchGuardian", route.params.id);
  } catch (error) {
    toast.error(t("guardians.edit.fetchError"));
    router.push({ name: 'guardians.index' });
  } finally {
    isLoading.value = false;
  }
};

const updateGuardian = async (formData) => {
  if (!hasChanges.value) {
    toast.info(t("guardians.edit.noChanges"));
    return;
  }

  isUpdating.value = true;
  try {
    await store.dispatch("guardian/updateGuardian", {
      id: route.params.id,
      guardianData: formData
    });
    toast.success(t("guardians.edit.updateSuccess"));
    router.push({ name: 'guardians.index' });
  } catch (error) {
    if (error.response?.status === 422) {
      toast.error(t("guardians.edit.validationError"));
    } else {
      toast.error(error.response?.data?.message || t("guardians.edit.updateError"));
    }
  } finally {
    isUpdating.value = false;
  }
};

const navigateBack = () => router.push({ name: 'guardians.index' });

const guardian = computed(() => {
  const guardianData = store.getters['guardian/getCurrentGuardian'];
  if (guardianData) {
    return {
      ...guardianData,
      password: '',
      password_confirmation: '',
      nationality_id: guardianData.nationality?.id || '',
      register_date: guardianData.register_date?.split('T')[0] || new Date().toISOString().split('T')[0]
    };
  }
  return null;
});

watch(guardian, (newGuardianData) => {
  if (newGuardianData && !originalGuardianData.value) {
    originalGuardianData.value = JSON.parse(JSON.stringify(newGuardianData));
    currentFormData.value = JSON.parse(JSON.stringify(newGuardianData));
  }
}, { immediate: true });

onMounted(fetchGuardian);
</script>
